import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Paper, Stack, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { dayLabel, quantity, sectionText } from '../labels';
import { Loader, SectionTitle, useConfirm, useLoad, useNotify, useUndoable } from '../common';
import { NewLocationDialog } from '../pantry/dialogs';
import { PrepareDialog, PrepareSaid } from '../pantry/PrepareDialog';

const NO_PLACE = 'Sem local';

/** Where a line is going: the suggestion until another place is picked. */
interface Choice { locationPublicId: T.Guid | null; asReserve: boolean; }

function subtitle(item: T.PutAwayItem, choice: Choice | undefined): string {
  const parts: string[] = [];
  const amount = quantity(item.quantity, item.unit);
  if (amount) parts.push(amount);
  if (item.isPutAway) parts.push(item.putAwayLocationName ? `arrumado em ${item.putAwayLocationName}` : 'arrumado sem local');
  else if (choice?.asReserve) parts.push(`reserva (o habitual é ${item.mainLocationName})`);
  else if (!choice?.locationPublicId) parts.push('escolhe o local');
  else if (choice.locationPublicId === item.suggestedLocationPublicId && item.suggestion === 'Usual') parts.push('local habitual');
  else if (choice.locationPublicId === item.suggestedLocationPublicId && item.suggestion === 'Section')
    parts.push(`sugerido: onde ficam outros de ${sectionText(item.storeSection).toLowerCase()}`);
  else if (item.mainLocationPublicId) parts.push(`novo local (era ${item.mainLocationName})`);
  if (!item.isPutAway && !item.tracksStock && choice?.locationPublicId) parts.push('entra no stock');
  return parts.join(' · ');
}

/**
 * Home from the store: what a paid trip brought, grouped by the place it goes to, so each cupboard is filled in one go.
 * The place is suggested (the item's usual place, or where items of its section are kept); clicking the name picks another.
 * What is put away at a place counts in the stock there; a new place is remembered for next time, "só desta vez" keeps a
 * reserve there. "Concluir" puts the rest away without saying where.
 */
const PutAwayPage: React.FC = () => {
  const { tripId = '' } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const offerUndo = useUndoable();
  const [confirm, confirmDialog] = useConfirm();
  const { data, setData, error, loading, reload } = useLoad(() => api.getPutAway(tripId), [tripId]);
  const [choices, setChoices] = useState<Record<T.Guid, Choice>>({});
  const [placeMenu, setPlaceMenu] = useState<{ anchor: HTMLElement; item: T.PutAwayItem } | null>(null);
  const [newPlaceFor, setNewPlaceFor] = useState<T.PutAwayItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState<T.PutAwayItem | null>(null);

  // Lines still to put away start at the suggested place; choices already made are kept
  useEffect(() => {
    if (!data) return;
    setChoices(previous => {
      const next: Record<T.Guid, Choice> = {};
      data.items.filter(i => !i.isPutAway).forEach(i => {
        next[i.itemPublicId] = previous[i.itemPublicId] ?? { locationPublicId: i.suggestedLocationPublicId ?? null, asReserve: false };
      });
      return next;
    });
  }, [data]);

  const locations = data?.locations ?? [];
  const locationName = (id: T.Guid | null) => locations.find(l => l.publicId === id)?.fullName ?? NO_PLACE;
  const pending = data?.items.filter(i => !i.isPutAway) ?? [];
  const done = data?.items.filter(i => i.isPutAway) ?? [];

  // Groups in the order of the places; "Sem local" last
  const order = new Map(locations.map((l, i) => [l.publicId, i]));
  const groups = Array.from(
    pending.reduce((map, item) => {
      const key = choices[item.itemPublicId]?.locationPublicId ?? '';
      map.set(key, [...(map.get(key) ?? []), item]);
      return map;
    }, new Map<string, T.PutAwayItem[]>()),
  ).sort(([a], [b]) => (a ? order.get(a) ?? 9998 : 9999) - (b ? order.get(b) ?? 9998 : 9999));

  /** `leaving`: the page closes after this, so "Desfazer" opens it again with the lines back. */
  const submit = async (items: T.PutAwayItem[], message: string, withPlaces = true, leaving = false) => {
    setBusy(true);
    setSaveError(null);
    try {
      const result = await api.putAway(tripId, items.map(i => ({
        itemPublicId: i.itemPublicId,
        locationPublicId: withPlaces ? choices[i.itemPublicId]?.locationPublicId ?? null : null,
        asReserve: withPlaces ? choices[i.itemPublicId]?.asReserve ?? false : false,
      })));
      setData(result);
      const undo = result.undo ?? [];
      if (undo.length === 0) notify(message, { severity: 'success' });
      else {
        offerUndo(message, () => api.undoPutAway(tripId, undo), back => {
          if (leaving) navigate(`/casa/idas/${tripId}/arrumar`);
          else setData(back);
        }, { severity: 'success' });
      }
      return true;
    } catch (e: any) {
      setSaveError(e.message ?? 'Ocorreu um erro.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Done: what is left is put away without saying where (its stock is left alone), or each at the place shown. */
  const finish = async (withPlaces: boolean) => {
    setFinishing(false);
    if (await submit(pending, 'Compras arrumadas', withPlaces, true)) navigate(`/casa/idas/${tripId}`);
  };

  /** Another place; away from its usual place: from now on, or only this time (as a reserve). */
  const choose = async (item: T.PutAwayItem, locationPublicId: T.Guid | null) => {
    setPlaceMenu(null);
    let asReserve = false;
    if (locationPublicId && item.mainLocationPublicId && locationPublicId !== item.mainLocationPublicId) {
      asReserve = !(await confirm({
        title: `${item.name} em ${locationName(locationPublicId)}`,
        text: `Passa a ficar aqui (deixa de ser ${item.mainLocationName}), ou só desta vez, como reserva?`,
        confirmText: 'Passa a ficar aqui',
        cancelText: 'Só desta vez',
      }));
    }
    setChoices(c => ({ ...c, [item.itemPublicId]: { locationPublicId, asReserve } }));
  };

  /**
   * "Preparar para sopa": part (or all) of what was bought cut into bags. What is left goes to the line's place;
   * all of it used, the line is done without a place.
   */
  const prepareLine = async (said: PrepareSaid) => {
    const item = preparing!;
    const choice = choices[item.itemPublicId];
    const allUsed = said.used >= said.preparation.suggestedUsed;
    setData(await api.putAway(tripId, [{
      itemPublicId: item.itemPublicId,
      locationPublicId: allUsed ? null : choice?.locationPublicId ?? null,
      asReserve: !allUsed && (choice?.asReserve ?? false),
      prepare: { bagPublicId: said.preparation.itemPublicId, used: said.used, bags: said.bags, locationPublicId: said.locationPublicId },
    }]));
    const bags = `${quantity(said.bags, said.preparation.unit)} de ${said.preparation.name}`;
    return allUsed ? bags : `${bags}; o resto em ${locationName(choice?.locationPublicId ?? null)}`;
  };

  const placeCreated = async (created: T.StorageLocation) => {
    const item = newPlaceFor;
    setNewPlaceFor(null);
    if (!data || !item) return;
    try {
      setData({ ...data, locations: await api.getStorageLocations() });
    } catch {
      setData({ ...data, locations: [...data.locations, created] });
    }
    await choose(item, created.publicId);
  };

  return (
    <>
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <IconButton aria-label="Voltar" onClick={() => navigate(`/casa/idas/${tripId}`)} sx={{ mt: -0.5 }}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Arrumar compras</Typography>
          <Typography variant="body2" color="text.secondary">
            {data ? `${data.storeName} · ${dayLabel(data.plannedDate)}. ` : ''}
            Diz onde arrumaste cada artigo: o círculo arruma-o no local do grupo, o nome escolhe outro local. O que vai para um local
            conta no stock de lá; o que não interessa fica sem local ao concluir.
          </Typography>
        </Box>
      </Stack>

      <Loader loading={loading} error={error} onRetry={reload} hasData={data !== undefined}>
        {data && (
          <>
            {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}
            {data.items.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Nesta ida não há artigos para arrumar.</Typography>
            )}
            {groups.map(([key, items]) => (
              <React.Fragment key={key || 'none'}>
                <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
                  <SectionTitle count={items.length}>{key ? locationName(key) : NO_PLACE}</SectionTitle>
                  {key && items.length > 1 && (
                    <Button size="small" disabled={busy} sx={{ mb: 0.25 }}
                      onClick={() => submit(items, `${items.length} artigos arrumados em ${locationName(key)}`)}>
                      Arrumar aqui
                    </Button>
                  )}
                </Stack>
                <Paper variant="outlined">
                  <List disablePadding>
                    {items.map(item => (
                      <ListItem key={item.itemPublicId} divider disablePadding>
                        <ListItemIcon sx={{ minWidth: 48, pl: 1 }}>
                          <IconButton
                            aria-label={`Arrumado: ${item.name}`}
                            disabled={busy}
                            onClick={() => submit([item], `${item.name}: ${locationName(choices[item.itemPublicId]?.locationPublicId ?? null)}`)}
                          >
                            <RadioButtonUncheckedIcon color="primary" />
                          </IconButton>
                        </ListItemIcon>
                        <ListItemButton onClick={e => setPlaceMenu({ anchor: e.currentTarget, item })}>
                          <ListItemText primary={item.name} secondary={subtitle(item, choices[item.itemPublicId])} />
                        </ListItemButton>
                        {item.preparedItems.length > 0 && (
                          <Chip label="Preparar para sopa" color="primary" variant="outlined" size="small" disabled={busy}
                            onClick={() => setPreparing(item)} sx={{ mr: 1.5, flexShrink: 0 }} />
                        )}
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </React.Fragment>
            ))}
            {done.length > 0 && (
              <>
                <SectionTitle count={done.length}>Arrumado</SectionTitle>
                <Paper variant="outlined">
                  <List disablePadding>
                    {done.map(item => (
                      <ListItem key={item.itemPublicId} divider>
                        <ListItemIcon sx={{ minWidth: 48, pl: 1 }}><CheckCircleIcon color="success" /></ListItemIcon>
                        <ListItemText
                          primary={<Box component="span" sx={{ color: 'text.disabled' }}>{item.name}</Box>}
                          secondary={subtitle(item, undefined)}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </>
            )}

            <Paper
              elevation={4}
              sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', zIndex: 2 }}
            >
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {pending.length === 0 ? 'Tudo arrumado' : pending.length === 1 ? 'Falta arrumar 1 artigo' : `Faltam arrumar ${pending.length} artigos`}
              </Typography>
              {pending.length > 0 && <Button variant="contained" onClick={() => setFinishing(true)} disabled={busy}>Concluir</Button>}
            </Paper>
          </>
        )}
      </Loader>

      <Menu anchorEl={placeMenu?.anchor} open={placeMenu !== null} onClose={() => setPlaceMenu(null)}>
        {placeMenu && <ListSubheader>{placeMenu.item.name}</ListSubheader>}
        {placeMenu && locations.map(l => (
          <MenuItem
            key={l.publicId}
            selected={choices[placeMenu.item.itemPublicId]?.locationPublicId === l.publicId}
            onClick={() => choose(placeMenu.item, l.publicId)}
            sx={{ pl: l.parentPublicId ? 4 : 2 }}
          >
            <ListItemText primary={l.name} secondary={l.publicId === placeMenu.item.mainLocationPublicId ? 'local habitual' : undefined} />
          </MenuItem>
        ))}
        {placeMenu && <MenuItem onClick={() => { setNewPlaceFor(placeMenu.item); setPlaceMenu(null); }}>Novo local…</MenuItem>}
        {placeMenu && (
          <MenuItem onClick={() => choose(placeMenu.item, null)}>
            <ListItemText primary={NO_PLACE} secondary="Arrumar sem registar onde" />
          </MenuItem>
        )}
      </Menu>
      <Dialog open={finishing} onClose={() => setFinishing(false)}>
        <DialogTitle>Concluir arrumação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pending.length === 1 ? 'O artigo que falta fica arrumado' : `Os ${pending.length} artigos que faltam ficam arrumados`} sem
            registar onde, e o stock não muda. Ou arruma cada um no local do grupo onde está, e conta no stock de lá.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishing(false)}>Cancelar</Button>
          {pending.some(i => choices[i.itemPublicId]?.locationPublicId) && (
            <Button onClick={() => finish(true)}>Nos locais indicados</Button>
          )}
          <Button variant="contained" onClick={() => finish(false)}>Concluir sem local</Button>
        </DialogActions>
      </Dialog>
      <PrepareDialog
        itemId={preparing?.catalogItemPublicId ?? null}
        tripItemId={preparing?.itemPublicId}
        save={prepareLine}
        onClose={() => setPreparing(null)}
        onDone={message => { setPreparing(null); notify(message, { severity: 'success' }); }}
      />
      <NewLocationDialog open={newPlaceFor !== null} locations={locations} onClose={() => setNewPlaceFor(null)} onCreated={placeCreated} />
      {confirmDialog}
    </>
  );
};

export default PutAwayPage;
